<script setup lang="ts">
import { helpers } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'

import {
  AuxiliarMeta,
  CustomerTypeEnum,
  DocumentTypeEnum,
  Form,
  Identification,
} from '~/layers/forms/models'
import { requiredIfValidator, requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Identification
  validationScope?: string
  ignoreValidations?: boolean
  registerValidationAs?: string
  entityType?: PropsTypesOf<typeof CustomerTypeEnum>['code'] | null
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  validationScope: Form.identification,
  registerValidationAs: Form.identification,
  entityType: null,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const v$ = useValidation(
  () =>
    props.ignoreValidations
      ? {}
      : {
          type: { required: requiredValidator },
          number: { required: requiredValidator },
          entityName: {
            required: requiredIfValidator(model.value.type === DocumentTypeEnum.R.code),
          },
          nationality: {
            selected: helpers.withMessage(
              'Seleccione una marca.',
              () => model.value.type !== DocumentTypeEnum.P.code || !!model.value.nationality?.id
            ),
          },
        },
  model,
  {
    $scope: props.validationScope,
    $registerAs: props.registerValidationAs,
  }
)

const { builder: dispatcherParamsBuilder } = useSiteApiDispatcherByProductParams()

const {
  execute: fetchNationalities,
  data: nationalities,
  status: nationalitiesStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/Nacionalidades',
  })
)

const {
  execute: fetchRNC,
  data: RNC,
  status: RNCStatus,
} = useSiteApi('/api/Auxiliares/RNC', {
  method: 'GET',
  query: computed(() => {
    const { number: RNC } = model.value
    console.log(RNC)
    return { RNC: RNC ?? undefined }
  }),
  key: () => crypto.randomUUID(),
  watch: false,
  immediate: false,
})

const getNationalities = () => {
  if (model.value.type === 'P' && !nationalities.value?.items?.length) fetchNationalities()
}

const padronResponse = ref<SiteApiSchema<'vwRNC'> | null>(null)
const padronStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle')

const searchRNC = async () => {
  if (!model.value.number) return

  await fetchRNC()
  padronResponse.value = RNC.value ?? null
  model.value.entityName = RNC.value?.razonSocial ?? null
  padronStatus.value = 'success'
}

watch(
  () => props.entityType,
  (n) => {
    if (n === CustomerTypeEnum.Business.code) model.value.type = DocumentTypeEnum.R.code
  },
  {
    immediate: true,
  }
)

const documentNumberMask = computed(() => {
  switch (model.value.type) {
    case DocumentTypeEnum.R.code:
      return undefined //TODO: Mask to be defined
    case DocumentTypeEnum.C.code:
      return '999-9999999-9'
    case DocumentTypeEnum.P.code:
      return undefined // TODO: Mask to be defined
    default:
      return undefined
  }
})

watch(
  () => model.value.type,
  () => {
    const { type } = model.value
    if (type) model.value.entityName = DocumentTypeEnum[type].label
    model.value.number = null
    getNationalities()
    model.value.nationality = AuxiliarMeta.load()
  }
)

watch(
  () => model.value.number,
  () => {
    model.value.entityName = null
  }
)

watch(
  () => model.value.nationality?.id,
  (n, o) => {
    if (!n || n === o) return
    const nationality = nationalities.value?.items?.find((item) => item.id === n)
    if (nationality?.id)
      model.value.nationality = {
        id: nationality?.id,
        code: nationality?.codigo,
        name: nationality?.nombreM,
      }
  }
)
</script>

<template>
  <InputField
    v-if="entityType === CustomerTypeEnum.Person.code"
    label="Tipo de documento"
    :error="v$.type?.$error"
    :error-message="v$.type?.$errors[0]?.$message.toString()"
  >
    <PvSelect
      v-model="model.type"
      option-label="label"
      option-value="code"
      :options="Identification.getTypeList(entityType)"
      :disabled="readonly"
    />
  </InputField>

  <InputField
    :label="
      entityType === CustomerTypeEnum.Business.code && model.type
        ? DocumentTypeEnum[model.type].label
        : 'Número de documento'
    "
    :error="v$.number?.$error"
    :error-message="v$.number?.$errors[0]?.$message.toString()"
    :loading="RNCStatus === 'pending'"
  >
    <PvInputGroup>
      <PvInputMask
        v-if="model.type === DocumentTypeEnum.C.code"
        v-model="model.number"
        :mask="documentNumberMask"
        :disabled="readonly"
      />
      <PvInputText
        v-else
        v-model="model.number"
        :disabled="readonly"
      />
      <PvButton
        v-if="model.type === DocumentTypeEnum.R.code"
        @click="readonly ? undefined : searchRNC"
      >
        <span class="icon-[ph--magnifying-glass-bold]" />
      </PvButton>
    </PvInputGroup>
  </InputField>

  <InputField
    v-if="
      model.type === DocumentTypeEnum.P.code &&
      entityType === CustomerTypeEnum.Person.code &&
      model.nationality
    "
    label="Nacionalidad"
    :loading="nationalitiesStatus === 'pending'"
    :error="v$.nationality?.$error"
    :error-message="v$.nationality?.$errors[0]?.$message.toString()"
  >
    <PvInputText
      v-if="readonly"
      v-model="model.nationality.name"
      no-hint
      disabled
    />

    <PvSelect
      v-else
      v-model="model.nationality.id"
      option-label="nombreM"
      option-value="id"
      :options="nationalities?.items ?? []"
    />
  </InputField>

  <InputField
    v-if="model.type === DocumentTypeEnum.R.code && entityType !== CustomerTypeEnum.Person.code"
    label="Empresa"
    :error="v$.entityName?.$error"
    :error-message="v$.entityName?.$errors[0]?.$message.toString()"
    :loading="padronStatus === 'pending'"
  >
    <PvInputText
      v-model="model.entityName"
      readonly
    />
  </InputField>
</template>
